import { Alert } from '@synoptic/ui-kit/alert/alert.js';
import { Button } from '@synoptic/ui-kit/button/button.js';
import { Input } from '@synoptic/ui-kit/input/input.js';
import { Textarea } from '@synoptic/ui-kit/textarea/textarea.js';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { AiFilterFormValues } from '../types';
import { CreateWithAIAssistantModal } from './create-with-ai-assistant';
import { TestAIFilterModal } from './test-ai-filter';
import {
  footerContainer,
  formActions,
  formContainer,
  formContent,
  formFieldDescription,
} from './manage-filter.css';

interface AIFilterFormProps {
  onSubmit: (form: AiFilterFormValues) => Promise<void>;
  onCancel: () => void;
  formData: UseFormReturn<AiFilterFormValues>;
}

export const AIFilterForm = ({
  formData,
  onSubmit,
  onCancel,
}: AIFilterFormProps) => {
  const { control, handleSubmit, formState, getValues, setValue } = formData;
  const prompt = useWatch({ control, name: 'prompt' });
  const canTest = prompt.trim().length > 0 && prompt.length <= 4000;

  return (
    <form
      className={formContainer}
      onSubmit={async (e) => {
        e.stopPropagation();
        if (formState.isSubmitting) {
          e.preventDefault();
          return;
        }
        await handleSubmit(onSubmit)(e);
      }}
    >
      <div className={formContent}>
        <Controller
          name="name"
          control={control}
          rules={{
            required: 'Name is required',
            maxLength: {
              value: 30,
              message: 'Name must be 30 characters or less',
            },
            validate: (value) => value.trim().length > 0 || 'Name is required',
          }}
          render={({ field }) => (
            <Input
              {...field}
              label="Name"
              placeholder="Enter filter name"
              maxLength={30}
              disabled={formState.isSubmitting}
              errorMessage={formState.errors.name?.message}
            />
          )}
        />
        <div>
          <Controller
            name="prompt"
            control={control}
            rules={{
              required: 'Question is required',
              maxLength: {
                value: 4000,
                message: 'Question must be 4000 characters or less',
              },
              validate: (value) =>
                value.trim().length > 0 || 'Question is required',
            }}
            render={({ field }) => (
              <Textarea
                {...field}
                autoResize
                rows={3}
                maxLength={4000}
                label="Question"
                placeholder="e.g., Is this breaking news?"
                disabled={formState.isSubmitting}
                errorMessage={formState.errors.prompt?.message}
              />
            )}
          />
          <p className={formFieldDescription}>
            Posts are included when the answer is yes.
          </p>
        </div>
        <div className={formActions}>
          <TestAIFilterModal
            disabled={!canTest || formState.isSubmitting}
            getFilterValues={getValues}
          />
          <CreateWithAIAssistantModal
            disabled={formState.isSubmitting}
            onSelectFilterSuggestion={(suggestion) => {
              setValue('prompt', suggestion.prompt, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
          />
        </div>
        {formState.errors.root?.message && (
          <Alert role="alert">{formState.errors.root.message}</Alert>
        )}
      </div>
      <footer className={footerContainer}>
        <Button
          onClick={onCancel}
          disabled={formState.isSubmitting}
          variant="secondary"
        >
          Cancel
        </Button>
        <Button
          disabled={!formState.isValid}
          loading={formState.isSubmitting}
          type="submit"
          variant="primary"
        >
          Save
        </Button>
      </footer>
    </form>
  );
};
